import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import OverviewCards from './OverviewCards';
import AssetAllocationChart from './AssetAllocationChart';
import Top10HoldingsChart from './Top10HoldingsChart';
import PerformanceChart from './PerformanceChart';
import './styles.css';
import { API_BASE_URL } from '../../lib/api';

// Configure axios to send cookies
axios.defaults.withCredentials = true;

/**
 * Analytics Dashboard Component
 * Displays portfolio overview cards, asset allocation charts, top holdings, and performance metrics
 */
function AnalyticsDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [portfolio, setPortfolio] = useState(null);
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [performancePeriod, setPerformancePeriod] = useState(30); // 30, 90, or 365 days

  // Fetch portfolio and holdings data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch portfolio with current prices (includes enriched holdings)
        const portfolioResponse = await axios.get(`${API_BASE_URL}/portfolio`, {
          withCredentials: true
        });
        console.log('[Analytics] Portfolio data received:', {
          summary: portfolioResponse.data.summary,
          holdingsCount: portfolioResponse.data.holdings?.length || 0
        });
        setPortfolio(portfolioResponse.data);

        // Fetch snapshots for performance chart (based on selected period)
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - performancePeriod);

        const snapshotsResponse = await axios.get(`${API_BASE_URL}/snapshots`, {
          params: {
            startDate: startDate.toISOString().split('T')[0],
            endDate: endDate.toISOString().split('T')[0]
          },
          withCredentials: true
        });

        if (snapshotsResponse.data.success) {
          // Sort snapshots by date ascending for chart
          const sortedSnapshots = snapshotsResponse.data.snapshots.sort((a, b) =>
            a.date.localeCompare(b.date)
          );
          setSnapshots(sortedSnapshots);
        }

        setLoading(false);
      } catch (err) {
        console.error('Error fetching analytics data:', err);
        setError(err.response?.data?.error || err.message || 'Failed to fetch analytics data');
        setLoading(false);
      }
    };

    if (user) {
      fetchData();
    }
  }, [user, performancePeriod]);

  // Calculate overview metrics
  const overviewMetrics = useMemo(() => {
    if (!portfolio) return null;

    const summary = portfolio.summary;

    // Calculate daily change from snapshots (if available)
    let dailyChange = 0;
    let dailyChangePercent = 0;
    if (snapshots.length > 0) {
      const latestSnapshot = snapshots[snapshots.length - 1];
      const previousSnapshot = snapshots.length > 1 ? snapshots[snapshots.length - 2] : null;

      if (previousSnapshot && latestSnapshot) {
        dailyChange = latestSnapshot.netWorth - previousSnapshot.netWorth;
        dailyChangePercent = previousSnapshot.netWorth > 0
          ? (dailyChange / previousSnapshot.netWorth) * 100
          : 0;
      }
    }

    const metrics = {
      totalValue: summary.netWorthEUR || 0,
      totalGainLoss: summary.totalGainEUR || 0,
      totalGainLossPercent: summary.totalGainLossPercentageEUR || 0,
      dailyChange,
      dailyChangePercent,
      holdingsCount: portfolio.holdings ? portfolio.holdings.filter(h => !h.isLiability).length : 0,
      currency: 'EUR'
    };

    console.log('[Analytics] Overview metrics calculated:', {
      totalGainLoss: metrics.totalGainLoss,
      totalGainLossPercent: metrics.totalGainLossPercent,
      totalValue: metrics.totalValue
    });

    return metrics;
  }, [portfolio, snapshots]);

  // Calculate asset allocation data
  const assetAllocation = useMemo(() => {
    if (!portfolio) return [];

    const breakdown = portfolio.summary.breakdownEUR || {};

    // Map breakdown to chart data format
    const allocationData = [
      { name: 'Stocks & ETFs', value: breakdown.stocks || 0, color: '#4F46E5' },
      { name: 'Cryptocurrency', value: breakdown.crypto || 0, color: '#F59E0B' },
      { name: 'Precious Metals', value: breakdown.preciousMetals || 0, color: '#FFD700' },
      { name: 'Cash', value: breakdown.cash || 0, color: '#10B981' },
      { name: 'Real Estate', value: breakdown.realEstate || 0, color: '#EC4899' },
      { name: 'Other Assets', value: breakdown.other || 0, color: '#8B5CF6' }
    ].filter(item => item.value > 0); // Only show categories with values

    return allocationData;
  }, [portfolio]);

  // Calculate top 10 holdings by value
  const top10Holdings = useMemo(() => {
    if (!portfolio || !portfolio.holdings || portfolio.holdings.length === 0) return [];

    // Use enriched holdings from portfolio (includes totalValueEUR)
    const enrichedHoldings = portfolio.holdings;

    // Filter out liabilities and sort by total value EUR
    const topHoldings = enrichedHoldings
      .filter(h => !h.isLiability && h.totalValueEUR > 0)
      .sort((a, b) => b.totalValueEUR - a.totalValueEUR)
      .slice(0, 10)
      .map(h => ({
        name: h.ticker || h.assetName,
        value: h.totalValueEUR,
        percentage: (h.totalValueEUR / portfolio.summary.totalAssetsEUR) * 100
      }));

    console.log('[Analytics] Top 10 holdings calculated:', {
      count: topHoldings.length,
      holdings: topHoldings.map(h => ({ name: h.name, value: h.value }))
    });

    return topHoldings;
  }, [portfolio]);

  // Prepare performance data for chart
  const performanceData = useMemo(() => {
    if (!snapshots.length) return [];

    return snapshots.map(snapshot => ({
      date: snapshot.date,
      value: snapshot.netWorth || snapshot.totalValue || 0
    }));
  }, [snapshots]);

  if (loading) {
    return (
      <div className="analytics-dashboard">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="analytics-dashboard">
        <div className="error-container">
          <p className="error-message">Error: {error}</p>
        </div>
      </div>
    );
  }

  if (!portfolio || !overviewMetrics) {
    return (
      <div className="analytics-dashboard">
        <div className="empty-state">
          <p>No portfolio data available. Add some holdings to get started!</p>
        </div>
      </div>
    );
  }

  return (
    <div className="analytics-dashboard">
      <header className="dashboard-header">
        <div className="header-content">
          <button
            className="back-button"
            onClick={() => navigate('/')}
            aria-label="Back to portfolio"
          >
            ← Back to Portfolio
          </button>
          <div>
            <h1>Portfolio Analytics</h1>
            <p className="dashboard-subtitle">Comprehensive overview of your investment portfolio</p>
          </div>
        </div>
      </header>

      {/* Overview Cards Section */}
      <section className="dashboard-section">
        <OverviewCards metrics={overviewMetrics} />
      </section>

      {/* Charts Grid */}
      <div className="charts-grid">
        {/* Asset Allocation Chart */}
        <section className="dashboard-section chart-section">
          <h2 className="section-title">Asset Allocation</h2>
          <AssetAllocationChart data={assetAllocation} />
        </section>

        {/* Top 10 Holdings Chart */}
        <section className="dashboard-section chart-section">
          <h2 className="section-title">Top 10 Holdings</h2>
          <Top10HoldingsChart data={top10Holdings} />
        </section>
      </div>

      {/* Performance Chart - Full Width */}
      <section className="dashboard-section performance-section">
        <div className="section-header">
          <h2 className="section-title">Portfolio Performance</h2>
          <div className="period-selector">
            <button
              className={performancePeriod === 30 ? 'active' : ''}
              onClick={() => setPerformancePeriod(30)}
              aria-label="Show 30 day performance"
            >
              30D
            </button>
            <button
              className={performancePeriod === 90 ? 'active' : ''}
              onClick={() => setPerformancePeriod(90)}
              aria-label="Show 90 day performance"
            >
              90D
            </button>
            <button
              className={performancePeriod === 365 ? 'active' : ''}
              onClick={() => setPerformancePeriod(365)}
              aria-label="Show 365 day performance"
            >
              1Y
            </button>
          </div>
        </div>
        <PerformanceChart data={performanceData} period={performancePeriod} />
      </section>
    </div>
  );
}

export default AnalyticsDashboard;
